import {
  IconEllipsisV,
  IconEnvelope,
  IconInvoice,
  IconPlus,
  IconPlusCircle,
} from '@maia/icons';
import {
  Button,
  ButtonMenu,
  CardList,
  PageHeader,
  showToast,
  Subnav,
  Tag,
  TextButton,
  Typography,
} from '@maia/ui';
import { createFileRoute, linkOptions, Outlet } from '@tanstack/react-router';

import { IAttachmentEntity, IQuotationEntity } from '@maia/shared';
import { fileToBase64, getFullName, RefProvider } from '@maia/utils';
import { useQuery } from '@tanstack/react-query';
import { getNotesOptions } from '../../../../features/quotation/notes/queryOptions';
import { getQuotationOptions } from './../../../../features/quotation/quotation/queryOptions';

import { CustomerInformation } from '../../../../features/shared/customer/customer-information';
import { useCreateNote } from './../../../../features/quotation/notes/create-note.options';
import { RecentNoteCard } from '../../../../features/shared/note/recent-note-card';
import { AddNoteSidedrawer } from './../../../../features/shared/note/add-note-sidedrawer';
import { NoteSidedrawer } from './../../../../features/shared/note/note-sidedrawer';
import { queryClient } from './../../../../main';
import { useDrawerStore } from './../../../../stores/drawer.store';
import { match, P } from 'ts-pattern';
import { useSendMail } from './../../../../features/quotation/quotation/send-mail.options';
import {
  useCooldown,
  useCooldownStore,
} from './../../../../stores/cooldown.store';
import { useCancel } from './../../../../features/quotation/quotation/cancel.options';
import { ActivitySidedrawer } from './../../../../features/shared/activity/activity-sidedrawer';
import { TaskSidedrawer } from './../../../../features/shared/task/task-sidedrawer';

export const Route = createFileRoute(
  '/_authenticated/quotations/$guid/_layout',
)({
  component: Layout,
  loader: async ({ context: { queryClient }, params: { guid } }) => {
    const quotation = await queryClient.ensureQueryData(
      getQuotationOptions(guid),
    );

    return { quotation };
  },
});

function Layout() {
  const { guid } = Route.useParams();
  const data = Route.useLoaderData();
  const navigate = Route.useNavigate();
  const { openDrawer, closeDrawer } = useDrawerStore();
  const setCooldown = useCooldownStore((s) => s.setCooldown);
  const { isCoolingDown, remainingSeconds } = useCooldown('quotation.mail');

  const { data: notes } = useQuery(
    getNotesOptions(guid, {
      page: 1,
      limit: 3,
      sort: 'created_at,desc',
    }),
  );

  const { data: quotation } = useQuery(getQuotationOptions(guid));

  const { mutateAsync: createNote } = useCreateNote(
    {
      customerGuid: data.quotation.customer.guid,
      quotationGuid: data.quotation.guid,
    },
    () => {
      queryClient.invalidateQueries({
        queryKey: ['notes', guid],
        exact: false,
      });
      showToast({
        intent: 'success',
        title: 'Notitie toegevoegd',
        config: { position: 'top-right' },
      });
      closeDrawer();
    },
    () => {
      showToast({
        intent: 'error',
        title: 'Er ging iets mis',
        description:
          'We konden niet de notitie voor je aanmaken. Probeer het later opnieuw',
        config: { position: 'top-right' },
      });
    },
  );

  const { mutateAsync: sendEmail, isPending } = useSendMail(
    () => {
      showToast({
        intent: 'success',
        title: 'Mail verstuurd',
        config: { position: 'top-right' },
      });
      setCooldown('quotation.mail');
    },
    () => {
      showToast({
        intent: 'error',
        title: 'Er ging iets mis',
        description: 'We konden niet de mail sturen. Probeer het later opnieuw',
        config: { position: 'top-right' },
      });
    },
  );

  const { mutateAsync: cancel, isPending: isCancelPending } = useCancel(
    () => {
      showToast({
        intent: 'success',
        title: 'Offerte afgewezen',
        config: { position: 'top-right' },
      });

      queryClient.invalidateQueries({
        queryKey: ['quotations', guid],
      });
    },
    () => {
      showToast({
        intent: 'error',
        title: 'Er ging iets mis',
        description:
          'We konden de offerte niet afwijzen. Probeer het later opnieuw',
        config: { position: 'top-right' },
      });
    },
  );

  const { mutateAsync: remove, isPending: isRemovePending } = useCancel(
    async () => {
      showToast({
        intent: 'success',
        title: 'Offerte verwijderd',
        config: { position: 'top-right' },
      });

      await queryClient.invalidateQueries({
        queryKey: ['quotations', guid],
      });

      await navigate({
        to: '/quotations',
      });
    },
    () => {
      showToast({
        intent: 'error',
        title: 'Er ging iets mis',
        description:
          'We konden de offerte niet verwijderen. Probeer het later opnieuw',
        config: { position: 'top-right' },
      });
    },
  );

  return (
    <div className="layout-sidebar">
      <PageHeader
        icon={IconInvoice}
        title={'Offerte'}
        reference={`#${data.quotation.reference}`}
        specs={[
          {
            text: 'Aangemaakt door',
            value: getFullName(data.quotation.createdBy),
          },
        ]}
        actions={
          <div className="flex gap-x-32">
            {match(quotation?.status.value as IQuotationEntity['status'])
              .with(P.union('concept', 'sent'), () => (
                <ButtonMenu
                  intent="tertiary"
                  size="default"
                  iconRight={IconEllipsisV}
                  isLoading={isCancelPending || isRemovePending}
                  isDisabled={isCancelPending || isRemovePending}
                  menuItems={[
                    {
                      icon: 'pen',
                      text: 'Offerte wijzigen',
                      onAction: () =>
                        navigate({
                          to: '/add-insurance/$customer/cart/$cart/product/$product',
                          params: {
                            cart: data.quotation.guid,
                            customer: data.quotation.customer.guid,
                            product: data.quotation.products[0].guid,
                          },
                        }),
                    },
                    {
                      icon: 'times',
                      text: 'Offerte afwijzen',
                      onAction: () =>
                        cancel({
                          customerGuid: data.quotation.customer.guid,
                          quotationGuid: data.quotation.guid,
                        }),
                    },
                    {
                      icon: 'trash-alt',
                      text: 'Offerte verwijderen',
                      intent: 'destructive',
                      onAction: () =>
                        remove({
                          customerGuid: data.quotation.customer.guid,
                          quotationGuid: data.quotation.guid,
                        }),
                    },
                  ]}
                >
                  Bewerk offerte
                </ButtonMenu>
              ))
              .otherwise(() => null)}

            {quotation?.status.value === 'sent' && (
              <Button
                intent="cta"
                size="default"
                isLoading={isPending}
                isDisabled={isPending || isCoolingDown}
                iconLeft={IconEnvelope}
                onPress={() =>
                  sendEmail({
                    customerGuid: data.quotation.customer.guid,
                    quotationGuid: data.quotation.guid,
                  })
                }
              >
                {isCoolingDown
                  ? `Offerte versturen (${remainingSeconds})`
                  : 'Offerte versturen'}
              </Button>
            )}
            <ButtonMenu
              intent="cta"
              size="default"
              iconLeft={IconPlus}
              menuItems={[
                {
                  icon: 'check-square',
                  text: 'Nieuwe taak',
                  href: `/tasks/new/quotation?guid=${data.quotation.guid}`,
                },
              ]}
            >
              Toevoegen
            </ButtonMenu>
          </div>
        }
        status={quotation?.status ? <Tag {...quotation.status} /> : undefined}
        className="area-page-header"
      />

      <Subnav
        className="area-page-subnav"
        links={[
          linkOptions({
            to: '/quotations/$guid',
            params: {
              guid,
            },
            label: 'Overzicht',
          }),
          linkOptions({
            to: '/quotations/$guid/premium-data',
            params: {
              guid,
            },
            label: 'Premiegegevens',
          }),
          linkOptions({
            to: '/quotations/$guid/activities',
            params: {
              guid,
            },
            label: 'Activiteit',
          }),
          linkOptions({
            to: '/quotations/$guid/tasks',
            params: {
              guid,
            },
            label: 'Taken',
          }),
          linkOptions({
            to: '/quotations/$guid/notes',
            params: {
              guid,
            },
            label: 'Notities',
          }),
        ]}
      />

      <div className="area-page-content content-grid">
        <Outlet />
      </div>

      <RefProvider<HTMLDivElement>>
        {({ ref, trackedElement }) => (
          <div className="area-page-sidebar flex flex-col gap-y-32" ref={ref}>
            <Typography
              variant="display6"
              weight="medium"
              className="text-primaryMain"
            >
              Klantgegevens
            </Typography>

            <CustomerInformation customer={data.quotation.customer} />

            <CardList
              title="Recente notities"
              items={notes?.response ?? []}
              render={(note) => (
                <RecentNoteCard
                  note={note}
                  onClick={() => openDrawer({ type: 'note', data: note })}
                />
              )}
              resource="recente notities"
              action={
                <TextButton
                  size="tiny"
                  iconLeft={IconPlusCircle}
                  onPress={() => openDrawer({ type: 'note.add' })}
                >
                  Voeg notitie toe
                </TextButton>
              }
            />
            <TaskSidedrawer portalRef={trackedElement} />
            <ActivitySidedrawer portalRef={trackedElement} />
            <NoteSidedrawer portalRef={trackedElement} />

            <AddNoteSidedrawer
              portalRef={trackedElement}
              onSubmit={async (values) => {
                const files: Array<IAttachmentEntity> = await Promise.all(
                  values.explanation.files.map(async (file) => {
                    const base64 = await fileToBase64(file);

                    return {
                      filename: file.name,
                      mimetype: file.type,
                      encoded_document: base64,
                    };
                  }),
                );

                await createNote({
                  category: values.type,
                  title: values.subject,
                  description: values.explanation.markdown,
                  attachments: files,
                });
              }}
            />
          </div>
        )}
      </RefProvider>
    </div>
  );
}
